import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { Message } from 'src/app/models/message.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from 'src/app/utils/api-data-source';

@Component({
  selector: 'app-message-view',
  templateUrl: './message-view.component.html',
  styleUrls: ['./message-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MessageViewComponent implements OnInit, AfterViewInit {
  message: Message = new Message();

  userListHeaders: string[] = ['contact_person', 'employer', 'is_read'];
  userListDataSource = new MessageUsersDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;

  constructor(
    public app: AppService,
    public api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService,
    private dialogRef: MatDialogRef<MessageViewComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {}

  ngOnInit(): void {
    // RBAC: Check messages:view permission before loading data.
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view messages.'
      );
      this.dialogRef.close();
      return;
    }

    // Get the message id
    this.message.id = Number(this.dialogData['messageId']);
    // Validate and make a request to get the message data
    if (!this.message.id) {
      this.utils.showToast(
        'We could not load the message. Please refresh the page and try again.'
      );
      this.dialogRef.close();
      return;
    }
  }

  ngAfterViewInit(): void {
    // Stop if the message id is not set.
    if (!this.message.id) {
      return;
    }

    // Make the API request to get the message.
    this.api
      .makeRequest('get', `v2/messages/${this.message.id}`)
      .then(
        (message: Message): void => {
          this.message.apply(message);
          this.userListDataSource.messageId = this.message.id;
          this.userListDataSource.getData(true);
        }
      )
      .catch(() => {
        this.utils.showToast('The message could not be opened.');
        this.dialogRef.close();
      });

    // Define the paginator tap events.
    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.userListDataSource.limit = paginator['pageSize'];
      this.userListDataSource.offset = paginator['pageIndex'];
      this.userListDataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void} This method does not return a value.
   */
  onOpenUrl(url: string): void {
    this.utils.openUrl(url);
  }

  /**
   * Notifies the user that the URL has been copied to their clipboard via a toast message.
   *
   * @return {void} Does not return any value.
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }
}

export class MessageUsersDataSource extends ApiDataSource {
  messageId: number = 0;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/messages/${this.messageId}/users`, resetOffset);
  }
}
